import { useDraggable } from '@dnd-kit/core';
import { GridRow, GridRowProps } from '@mui/x-data-grid-pro';
import { forwardRef } from 'react';
import { mergeReferences, getDraggingStyles, getSelectedRowIds, DND_OPACITY } from '@/components/dnd';
import { useReportDrag } from '../Report/ReportDragContext';

export interface DraggableRowProperties extends GridRowProps {
  isDragEnabled?: boolean;
}

export const DraggableRow = forwardRef<HTMLDivElement, DraggableRowProperties>((properties, reference) => {
  const { isDragEnabled = true, ...gridRowProperties } = properties;
  const { groupId, gridApiReference } = useReportDrag();

  const rowId = String(gridRowProperties.rowId);
  const selectionModel = gridApiReference.current?.state.rowSelection;

  const selectedIds = getSelectedRowIds(
    selectionModel as { type: 'include' | 'exclude'; ids: Set<string | number> } | undefined,
    () => gridApiReference.current?.getAllRowIds() || []
  );

  const ids = selectedIds.length > 0 && selectedIds.includes(rowId) ? selectedIds : [rowId];
  const rows = ids.map(id => gridApiReference.current?.getRow(id)).filter(Boolean);

  const dragData = {
    ids,
    rows,
    sourceGroupId: groupId,
    count: ids.length,
  };

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: rowId,
    data: dragData,
    disabled: !isDragEnabled || !groupId,
  });

  const canDrag = isDragEnabled && !!groupId;

  return (
    <GridRow
      {...gridRowProperties}
      {...attributes}
      {...listeners}
      ref={mergeReferences(setNodeRef, reference)}
      style={getDraggingStyles({
        isDragging,
        isDragEnabled: canDrag,
        baseStyle: gridRowProperties.style,
        opacity: DND_OPACITY.draggingLight,
      })}
    />
  );
});

DraggableRow.displayName = 'DraggableRow';
